/**
 * Job card print templates and label options.
 * Used by Settings (Templates tab) and VehicleDetailModal (Print Job Card).
 */

export const JOB_CARD_TEMPLATE_IDS = ['classic', 'minimal', 'compact'];

export const JOB_CARD_TEMPLATES = [
  { id: 'classic', name: 'Classic', description: 'Two-column grid, gold reg badge, full sections' },
  { id: 'minimal', name: 'Minimal', description: 'Clean single column, subtle borders' },
  { id: 'compact', name: 'Compact', description: 'Dense layout, smaller text, fits more on one page' }
];

export const DEFAULT_JOB_CARD_LABELS = {
  title: 'Job Card',
  vehicle: 'Vehicle',
  customerName: 'Customer Name',
  contact: 'Customer Contact',
  status: 'Status',
  collectionDate: 'Collection Date',
  technician: 'Technician',
  motStatus: 'MOT Status',
  workSummary: 'Work Summary',
  costBreakdown: 'Cost Breakdown',
  baseCost: 'Base Cost',
  prepCost: 'Prep Cost',
  warranty: 'Warranty',
  miscellaneous: 'Miscellaneous',
  totalCost: 'Total Cost',
  notes: 'Notes'
};

const formatDate = (dateString) => {
  if (!dateString) return 'Not set';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function getLabels(customLabels) {
  return { ...DEFAULT_JOB_CARD_LABELS, ...(customLabels || {}) };
}

function logoImg(logoUrl) {
  if (!logoUrl) return '';
  return `<img src="${logoUrl}" alt="Logo" class="job-card-logo" />`;
}

/** Tyre details section HTML for job card and invoice (when status is Tyres). Compact so job card fits on one page. Inline styles for invoice print. */
function tyreDetailsHTML(vehicle) {
  if (!vehicle || vehicle.status !== 'Tyres') return '';
  const v = (key) => (vehicle[key] != null && String(vehicle[key]).trim() !== '' ? String(vehicle[key]).trim() : '—');
  const tread = (pos) => v(`tread${pos}`);
  const cellStyle = 'padding: 2px 6px; border: 1px solid #ddd; font-size: 9pt;';
  return `
    <div class="job-card-tyre-details" style="margin-top: 10px; padding-top: 10px; border-top: 2px solid #333; font-size: 9pt;">
      <div class="job-card-tyre-heading" style="font-weight: 700; margin-bottom: 4px;">Tyre Tread Depths</div>
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 8px;"><tr><td style="${cellStyle}">FL</td><td style="${cellStyle}">FR</td><td style="${cellStyle}">RL</td><td style="${cellStyle}">RR</td><td style="${cellStyle}">SP</td></tr><tr><td style="${cellStyle}">${tread('FL')}</td><td style="${cellStyle}">${tread('FR')}</td><td style="${cellStyle}">${tread('RL')}</td><td style="${cellStyle}">${tread('RR')}</td><td style="${cellStyle}">${tread('SP')}</td></tr></table>
    </div>`;
}

/** Classic: two-column grid, gold reg badge (current default design) */
function classicHTML(vehicle, labels, logoUrl) {
  const L = getLabels(labels);
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${L.title} - ${vehicle.reg}</title>
      <style>
        @page { size: A4; margin: 10mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 100%; min-width: 100%; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; background: white; color: black; font-size: 11pt; line-height: 1.4; }
        .job-card { width: 100%; max-width: 100%; background: white; border: 2px solid #000; border-radius: 8px; padding: 20px; page-break-inside: avoid; }
        .job-card-with-tyres { padding: 12px; }
        .job-card-with-tyres .job-card-header { padding-bottom: 8px; margin-bottom: 12px; }
        .job-card-with-tyres .job-card-content { gap: 10px; }
        .job-card-with-tyres .job-card-full-width { padding-top: 8px; margin-top: 6px; }
        .job-card-with-tyres .job-card-notes { min-height: 36px; font-size: 10pt; }
        .job-card-header { border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; gap: 16px; }
        .job-card-header-left { display: flex; align-items: center; gap: 16px; flex: 1; }
        .job-card-logo { max-height: 56px; max-width: 180px; object-fit: contain; }
        .job-card-with-tyres .job-card-logo { max-height: 44px; max-width: 140px; }
        .job-card-title { font-size: 24pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; }
        .job-card-with-tyres .job-card-title { font-size: 18pt; }
        .job-card-reg { font-size: 18pt; font-weight: 700; letter-spacing: 0.15em; color: #000; background: #FFD700; padding: 8px 12px; border: 2px solid #000; border-radius: 6px; display: inline-block; flex-shrink: 0; }
        .job-card-with-tyres .job-card-reg { font-size: 14pt; padding: 6px 10px; }
        .job-card-content { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .job-card-section { display: flex; flex-direction: column; gap: 10px; }
        .job-card-label { font-size: 9pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: #666; margin-bottom: 5px; }
        .job-card-value { font-size: 12pt; font-weight: 500; color: #000; line-height: 1.4; min-height: 20px; }
        .job-card-full-width { grid-column: 1 / -1; border-top: 1px solid #ccc; padding-top: 15px; margin-top: 10px; }
        .job-card-notes { min-height: 80px; white-space: pre-wrap; font-size: 11pt; }
        .job-card-status { display: inline-block; padding: 6px 12px; border: 1px solid #000; border-radius: 4px; font-size: 10pt; font-weight: 600; text-transform: uppercase; background: #f5f5f5; }
        .job-card-tyre-details { margin-top: 10px; padding-top: 10px; border-top: 2px solid #333; font-size: 9pt; }
        .job-card-tyre-heading { font-weight: 700; margin-bottom: 4px; font-size: 9pt; }
        .job-card-tyre-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 9pt; }
        .job-card-tyre-table td { padding: 2px 6px; border: 1px solid #ddd; }
      </style>
    </head>
    <body>
      <div class="job-card${vehicle.status === 'Tyres' ? ' job-card-with-tyres' : ''}">
        <div class="job-card-header">
          <div class="job-card-header-left">
            ${logoImg(logoUrl)}
            <div class="job-card-title">${L.title}</div>
          </div>
          <div class="job-card-reg">${vehicle.reg || 'N/A'}</div>
        </div>
        <div class="job-card-content">
          <div class="job-card-section"><div class="job-card-label">${L.vehicle}</div><div class="job-card-value">${vehicle.vehicle || 'Not specified'}</div></div>
          <div class="job-card-section"><div class="job-card-label">${L.customerName}</div><div class="job-card-value">${vehicle.customerName || 'Not specified'}</div></div>
          <div class="job-card-section"><div class="job-card-label">${L.contact}</div><div class="job-card-value">${vehicle.contact || 'Not specified'}</div></div>
          <div class="job-card-section"><div class="job-card-label">${L.status}</div><div class="job-card-value"><span class="job-card-status">${vehicle.status || 'Workshop'}</span></div></div>
          <div class="job-card-section"><div class="job-card-label">${L.collectionDate}</div><div class="job-card-value">${formatDate(vehicle.collectionDate)}</div></div>
          <div class="job-card-section"><div class="job-card-label">${L.technician}</div><div class="job-card-value">${vehicle.technician || 'Not assigned'}</div></div>
          <div class="job-card-section"><div class="job-card-label">${L.motStatus}</div><div class="job-card-value">${vehicle.motStatus || 'Not specified'}</div></div>
          <div class="job-card-full-width"><div class="job-card-label">${L.workSummary}</div><div class="job-card-value">${vehicle.summary || 'No summary provided'}</div></div>
          <div class="job-card-full-width"><div class="job-card-label">${L.notes}</div><div class="job-card-value job-card-notes">${vehicle.notes || 'No additional notes'}</div></div>
          ${tyreDetailsHTML(vehicle)}
        </div>
      </div>
    </body>
    </html>`;
}

/** Minimal: single column, lighter borders */
function minimalHTML(vehicle, labels, logoUrl) {
  const L = getLabels(labels);
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${L.title} - ${vehicle.reg}</title>
      <style>
        @page { size: A4; margin: 10mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 100%; min-width: 100%; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Arial, sans-serif; background: white; color: #333; font-size: 11pt; line-height: 1.5; }
        .job-card { width: 100%; max-width: 100%; padding: 24px; page-break-inside: avoid; }
        .job-card-with-tyres { padding: 12px; }
        .job-card-with-tyres .job-card-header { margin-bottom: 12px; padding-bottom: 10px; }
        .job-card-with-tyres .job-card-row { padding: 6px 0; }
        .job-card-with-tyres .job-card-block { margin-top: 10px; padding-top: 10px; }
        .job-card-with-tyres .job-card-notes { min-height: 36px; }
        .job-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #ddd; gap: 16px; }
        .job-card-header-left { display: flex; align-items: center; gap: 16px; flex: 1; }
        .job-card-logo { max-height: 48px; max-width: 160px; object-fit: contain; }
        .job-card-with-tyres .job-card-logo { max-height: 40px; max-width: 130px; }
        .job-card-title { font-size: 20pt; font-weight: 600; color: #111; }
        .job-card-with-tyres .job-card-title { font-size: 16pt; }
        .job-card-reg { font-size: 14pt; font-weight: 600; color: #1a1a1a; background: #f0f0f0; padding: 6px 14px; border-radius: 6px; border: 1px solid #ccc; flex-shrink: 0; }
        .job-card-row { display: flex; padding: 10px 0; border-bottom: 1px solid #eee; }
        .job-card-label { font-size: 9pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #888; width: 140px; flex-shrink: 0; }
        .job-card-value { font-size: 11pt; color: #000; }
        .job-card-status { display: inline-block; padding: 4px 10px; border-radius: 4px; font-size: 9pt; font-weight: 600; background: #e8e8e8; color: #333; }
        .job-card-block { margin-top: 16px; padding-top: 16px; border-top: 1px solid #eee; }
        .job-card-notes { white-space: pre-wrap; font-size: 10pt; color: #555; min-height: 60px; }
        .job-card-tyre-details { margin-top: 10px; padding-top: 10px; border-top: 2px solid #333; font-size: 9pt; }
        .job-card-tyre-heading { font-weight: 700; margin-bottom: 4px; font-size: 9pt; }
        .job-card-tyre-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 9pt; }
        .job-card-tyre-table td { padding: 2px 6px; border: 1px solid #ddd; }
      </style>
    </head>
    <body>
      <div class="job-card${vehicle.status === 'Tyres' ? ' job-card-with-tyres' : ''}">
        <div class="job-card-header">
          <div class="job-card-header-left">
            ${logoImg(logoUrl)}
            <div class="job-card-title">${L.title}</div>
          </div>
          <div class="job-card-reg">${vehicle.reg || 'N/A'}</div>
        </div>
        <div class="job-card-row"><span class="job-card-label">${L.vehicle}</span><span class="job-card-value">${vehicle.vehicle || 'Not specified'}</span></div>
        <div class="job-card-row"><span class="job-card-label">${L.customerName}</span><span class="job-card-value">${vehicle.customerName || 'Not specified'}</span></div>
        <div class="job-card-row"><span class="job-card-label">${L.contact}</span><span class="job-card-value">${vehicle.contact || 'Not specified'}</span></div>
        <div class="job-card-row"><span class="job-card-label">${L.status}</span><span class="job-card-value"><span class="job-card-status">${vehicle.status || 'Workshop'}</span></span></div>
        <div class="job-card-row"><span class="job-card-label">${L.collectionDate}</span><span class="job-card-value">${formatDate(vehicle.collectionDate)}</span></div>
        <div class="job-card-row"><span class="job-card-label">${L.technician}</span><span class="job-card-value">${vehicle.technician || 'Not assigned'}</span></div>
        <div class="job-card-row"><span class="job-card-label">${L.motStatus}</span><span class="job-card-value">${vehicle.motStatus || 'Not specified'}</span></div>
        <div class="job-card-block"><div class="job-card-label" style="width:auto;margin-bottom:6px">${L.workSummary}</div><div class="job-card-value">${vehicle.summary || 'No summary provided'}</div></div>
        <div class="job-card-block"><div class="job-card-label" style="width:auto;margin-bottom:6px">${L.notes}</div><div class="job-card-value job-card-notes">${vehicle.notes || 'No additional notes'}</div></div>
        ${tyreDetailsHTML(vehicle)}
      </div>
    </body>
    </html>`;
}

/** Compact: dense, smaller fonts */
function compactHTML(vehicle, labels, logoUrl) {
  const L = getLabels(labels);
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${L.title} - ${vehicle.reg}</title>
      <style>
        @page { size: A4; margin: 10mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 100%; min-width: 100%; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; background: white; color: black; font-size: 9pt; line-height: 1.3; }
        .job-card { width: 100%; max-width: 100%; border: 1px solid #000; padding: 12px; page-break-inside: avoid; }
        .job-card-with-tyres { padding: 8px; }
        .job-card-with-tyres .job-card-header { margin-bottom: 8px; padding-bottom: 6px; }
        .job-card-with-tyres .grid { gap: 6px 12px; }
        .job-card-with-tyres .full { padding-top: 6px; margin-top: 4px; }
        .job-card-with-tyres .notes { min-height: 28px; }
        .job-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #000; gap: 12px; }
        .job-card-header-left { display: flex; align-items: center; gap: 12px; flex: 1; }
        .job-card-logo { max-height: 40px; max-width: 140px; object-fit: contain; }
        .job-card-with-tyres .job-card-logo { max-height: 32px; max-width: 100px; }
        .job-card-title { font-size: 16pt; font-weight: 700; text-transform: uppercase; }
        .job-card-with-tyres .job-card-title { font-size: 14pt; }
        .job-card-reg { font-size: 12pt; font-weight: 700; background: #FFD700; padding: 4px 8px; border: 1px solid #000; flex-shrink: 0; }
        .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px 16px; }
        .cell-label { font-size: 7pt; font-weight: 600; text-transform: uppercase; color: #555; }
        .cell-value { font-size: 9pt; }
        .full { grid-column: 1 / -1; padding-top: 8px; margin-top: 4px; border-top: 1px solid #ccc; }
        .notes { white-space: pre-wrap; min-height: 40px; font-size: 8pt; }
        .job-card-tyre-details { margin-top: 8px; padding-top: 8px; border-top: 2px solid #333; font-size: 8pt; }
        .job-card-tyre-heading { font-weight: 700; margin-bottom: 2px; font-size: 8pt; }
        .job-card-tyre-table { width: 100%; border-collapse: collapse; margin-bottom: 6px; font-size: 8pt; }
        .job-card-tyre-table td { padding: 2px 4px; border: 1px solid #ddd; }
      </style>
    </head>
    <body>
      <div class="job-card${vehicle.status === 'Tyres' ? ' job-card-with-tyres' : ''}">
        <div class="job-card-header">
          <div class="job-card-header-left">
            ${logoImg(logoUrl)}
            <div class="job-card-title">${L.title}</div>
          </div>
          <div class="job-card-reg">${vehicle.reg || 'N/A'}</div>
        </div>
        <div class="grid">
          <div><div class="cell-label">${L.vehicle}</div><div class="cell-value">${vehicle.vehicle || '—'}</div></div>
          <div><div class="cell-label">${L.customerName}</div><div class="cell-value">${vehicle.customerName || '—'}</div></div>
          <div><div class="cell-label">${L.contact}</div><div class="cell-value">${vehicle.contact || '—'}</div></div>
          <div><div class="cell-label">${L.status}</div><div class="cell-value">${vehicle.status || 'Workshop'}</div></div>
          <div><div class="cell-label">${L.collectionDate}</div><div class="cell-value">${formatDate(vehicle.collectionDate)}</div></div>
          <div><div class="cell-label">${L.technician}</div><div class="cell-value">${vehicle.technician || '—'}</div></div>
          <div><div class="cell-label">${L.motStatus}</div><div class="cell-value">${vehicle.motStatus || '—'}</div></div>
          <div class="full"><div class="cell-label">${L.workSummary}</div><div class="cell-value">${vehicle.summary || '—'}</div></div>
          <div class="full"><div class="cell-label">${L.notes}</div><div class="cell-value notes">${vehicle.notes || '—'}</div></div>
          ${tyreDetailsHTML(vehicle)}
        </div>
      </div>
    </body>
    </html>`;
}

const generators = {
  classic: classicHTML,
  minimal: minimalHTML,
  compact: compactHTML
};

/** Export for use in invoice print when vehicle status is Tyres */
export { tyreDetailsHTML };

/**
 * Generate full HTML for a job card print.
 * @param {Object} vehicle - Vehicle data
 * @param {Object} options - { templateId, customLabels, logoUrl }
 * @returns {string} Full HTML document
 */
export function generateJobCardHTML(vehicle, options = {}) {
  const templateId = options.templateId && generators[options.templateId] ? options.templateId : 'classic';
  const customLabels = options.customLabels || {};
  const logoUrl = options.logoUrl || null;
  return generators[templateId](vehicle, customLabels, logoUrl);
}

/** Sample vehicle for preview in Settings */
export const SAMPLE_VEHICLE = {
  reg: 'AB12 CDE',
  vehicle: '2020 Ford Focus 1.0 EcoBoost',
  customerName: 'John Smith',
  contact: '07700 900123',
  status: 'Workshop',
  collectionDate: '2025-02-15',
  technician: 'Mike Johnson',
  motStatus: 'Valid',
  summary: 'Service and MOT. Brake pads replaced.',
  cost: 150,
  prepCost: 0,
  warranty: 0,
  misc: 25,
  notes: 'Customer requested call when ready.'
};
